// ── agencyDb.js ──────────────────────────────────────────────────────────────
// All agency data layer functions. Each function takes `supabase` as first param
// so callers can pass the authenticated client.

// ── Agency ───────────────────────────────────────────────────────────────────

export async function getOrCreateAgency(supabase, ownerId) {
  const { data, error } = await supabase
    .from('agencies')
    .select('*')
    .eq('owner_id', ownerId)
    .maybeSingle();
  if (error) throw error;
  if (data) return data;
  const { data: created, error: createErr } = await supabase
    .from('agencies')
    .insert({ owner_id: ownerId, agency_name: '', agency_email: '' })
    .select()
    .single();
  if (createErr) throw createErr;
  return created;
}

export async function saveAgency(supabase, ownerId, fields) {
  const { data, error } = await supabase
    .from('agencies')
    .upsert({ owner_id: ownerId, ...fields }, { onConflict: 'owner_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Clients ──────────────────────────────────────────────────────────────────

export async function getAgencyClients(supabase, agencyId, { includeArchived = false } = {}) {
  let query = supabase
    .from('agency_clients')
    .select('*')
    .eq('agency_id', agencyId)
    .order('created_at', { ascending: false });
  if (!includeArchived) query = query.neq('status', 'archived');
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function addClientManually(supabase, agencyId, fields) {
  const { data, error } = await supabase
    .from('agency_clients')
    .insert({ agency_id: agencyId, status: 'active', ...fields })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function generateClientInvite(supabase, clientId) {
  const token = crypto.randomUUID();
  const { data, error } = await supabase
    .from('agency_clients')
    .update({ invite_token: token, invite_sent_at: new Date().toISOString(), status: 'pending' })
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateClient(supabase, clientId, fields) {
  const { data, error } = await supabase
    .from('agency_clients')
    .update(fields)
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function archiveClient(supabase, clientId) {
  return updateClient(supabase, clientId, { status: 'archived' });
}

// Look up client invite by token (public — no RLS bypass needed)
export async function lookupClientInvite(supabase, token) {
  const { data, error } = await supabase
    .from('agency_clients')
    .select('*, agencies(agency_name, white_label_enabled, white_label_name, white_label_color, white_label_logo_url)')
    .eq('invite_token', token)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function acceptClientInvite(supabase, token, userId) {
  const { data, error } = await supabase
    .from('agency_clients')
    .update({
      user_id: userId,
      invite_accepted_at: new Date().toISOString(),
      status: 'active',
    })
    .eq('invite_token', token)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Snapshots ─────────────────────────────────────────────────────────────────

export async function saveProgressSnapshot(supabase, agencyId, agencyClientId, snapshotData) {
  const { data, error } = await supabase
    .from('client_progress_snapshots')
    .insert({
      agency_id: agencyId,
      agency_client_id: agencyClientId,
      framework: snapshotData.framework || 'cmmc_l2',
      snapshot_data: snapshotData,
      sprs_score: snapshotData.sprsScore ?? null,
      compliance_pct: snapshotData.compliancePct ?? null,
      tasks_total: snapshotData.tasksTotal ?? 0,
      tasks_complete: snapshotData.tasksComplete ?? 0,
      captured_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getClientSnapshots(supabase, agencyClientId, limit = 10) {
  const { data, error } = await supabase
    .from('client_progress_snapshots')
    .select('*')
    .eq('agency_client_id', agencyClientId)
    .order('captured_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function getLatestSnapshots(supabase, agencyId) {
  // Get the most recent snapshot per client
  const { data, error } = await supabase
    .from('client_progress_snapshots')
    .select('*')
    .eq('agency_id', agencyId)
    .order('captured_at', { ascending: false });
  if (error) throw error;
  // Deduplicate: keep first (latest) per agency_client_id
  const seen = new Set();
  return (data || []).filter(s => {
    if (seen.has(s.agency_client_id)) return false;
    seen.add(s.agency_client_id);
    return true;
  });
}

// ── Logo upload ───────────────────────────────────────────────────────────────

export async function uploadAgencyLogo(supabase, ownerId, file) {
  const ext  = file.name.split('.').pop();
  const path = `${ownerId}/logo-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('agency-assets')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data: urlData } = supabase.storage
    .from('agency-assets')
    .getPublicUrl(path);
  return urlData.publicUrl;
}

// ── Activity log ──────────────────────────────────────────────────────────────

export async function logAgencyActivity(supabase, agencyId, actorId, eventType, description, metadata = {}) {
  const { error } = await supabase
    .from('agency_activity')
    .insert({ agency_id: agencyId, actor_id: actorId, event_type: eventType, description, metadata });
  if (error) console.error('logAgencyActivity:', error);
}

export async function getAgencyActivity(supabase, agencyId, limit = 50) {
  const { data, error } = await supabase
    .from('agency_activity')
    .select('*')
    .eq('agency_id', agencyId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export async function getAgencyStats(supabase, agencyId) {
  const [clientsRes, snapshotsRes] = await Promise.all([
    supabase.from('agency_clients').select('id, status, cmmc_level, contract_end').eq('agency_id', agencyId),
    supabase.from('client_progress_snapshots').select('agency_client_id, compliance_pct, sprs_score, captured_at').eq('agency_id', agencyId).order('captured_at', { ascending: false }),
  ]);

  const clients   = clientsRes.data  || [];
  const snapshots = snapshotsRes.data || [];

  const active   = clients.filter(c => c.status === 'active').length;
  const pending  = clients.filter(c => c.status === 'pending').length;
  const archived = clients.filter(c => c.status === 'archived').length;

  // Latest snapshot per client
  const seen = new Set();
  const latest = snapshots.filter(s => {
    if (seen.has(s.agency_client_id)) return false;
    seen.add(s.agency_client_id);
    return true;
  });

  const avgCompliance = latest.length
    ? Math.round(latest.reduce((sum, s) => sum + (s.compliance_pct || 0), 0) / latest.length)
    : 0;

  const atRisk = latest.filter(s => (s.compliance_pct || 0) < 50).length;

  // Contracts expiring in next 30 days
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const expiringContracts = clients.filter(c => {
    if (!c.contract_end) return false;
    const d = new Date(c.contract_end);
    return d >= now && d <= in30;
  }).length;

  return { active, pending, archived, avgCompliance, atRisk, expiringContracts };
}

// ── Ensure bucket exists (runtime check) ─────────────────────────────────────

export async function ensureAgencyAssetsBucket(supabase) {
  const { data } = await supabase.storage.getBucket('agency-assets');
  if (!data) {
    await supabase.storage.createBucket('agency-assets', { public: true, fileSizeLimit: 5 * 1024 * 1024 });
  }
}
